
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import authSession from "../../lib/authSession";
import { invalidateAdminCache } from "../../lib/adminApiCache";

// ── Default auth state fields ─────────────────────────────────────────────────
export const authInitialState = {
  // Session is cookie-based: the refresh token is an httpOnly cookie and the
  // access token lives in memory (authSession) — nothing in localStorage.
  isAuthenticated: false,
  token: null,
  isAdmin: false,
  user: null,
  loginLoading: false,
  loginError: null,
};

/**
 * NOTE: the admin login is cookie-only — the API sets the auth cookies and no
 * token is read from (or written to) the response body or localStorage.
 */

/**
 * Recursively search a parsed JSON payload for a user object.
 * Looks for the largest object that has recognisable user fields
 * (first_name, id, email, name).
 */
function findUser(node, visited = new Set()) {
  if (node === null || typeof node !== "object" || Array.isArray(node)) return undefined;
  if (visited.has(node)) return undefined;
  visited.add(node);
  const userLike = node.first_name || node.last_name || node.id || node.name;
  if (
    userLike !== undefined &&
    (Object.prototype.hasOwnProperty.call(node, "first_name") ||
      Object.prototype.hasOwnProperty.call(node, "email") ||
      Object.prototype.hasOwnProperty.call(node, "name"))
  ) {
    return node;
  }
  for (const key of ["data", "admin", "auth", "user", "payload", "response", "body", "result"]) {
    const nested = node[key];
    if (nested && typeof nested === "object") {
      const found = findUser(nested, visited);
      if (found !== undefined) return found;
    }
  }
  for (const value of Object.values(node)) {
    if (value && typeof value === "object") {
      const found = findUser(value, visited);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/**
 * Mirror the admin *display* keys the admin UI reads (`isAdmin` in
 * UserNavBar/AdminCourseRead, `email` for the login form prefill, `name`/`image`
 * for the nav bar). No tokens are stored — auth lives in httpOnly cookies.
 */
function persistAdminSession({ email, user }) {
  try {
    localStorage.setItem("isAdmin", "true");
    localStorage.setItem("email", email || user?.email || "");
    localStorage.setItem(
      "name",
      user
        ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name || "Admin"
        : "Admin"
    );
    localStorage.setItem("image", user?.image || "");
  } catch {
    /* storage unavailable — the session still lives in authSession/memory */
  }
}

/** Remove the flat admin keys on logout. */
function clearAdminSession() {
  try {
    for (const key of ["token", "refreshToken", "isAdmin", "email", "name", "image"]) {
      localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Action factory for the auth slice.
 * Returns the auth actions to be spread onto the Zustand store.
 */
export function createAuthActions(set, get) {
  return {
    /** POST /api/admin/login — the only place the auth endpoint lives. */
    login: async (email, password) => {
      set({ loginLoading: true, loginError: null });
      try {
        // Auth endpoint → cookies only (the API sets/refreshes the cookies).
        const response = await authSession.authFetch(`${API_URL}/api/admin/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const json = await response.json();
        // Detect login failure either from the HTTP status, an explicit
        // `error`/`message` field, or a nested `{ message: "..." }` object.
        if (
          !response.ok ||
          json.error ||
          json.success === false ||
          json.status === "error" ||
          json.status === false
        ) {
          const err = json.error || json.message || json.statusMessage || "Login failed";
          const msg = toDisplayString(err, "Login failed");
          set({ loginLoading: false, loginError: msg });
          return { success: false, error: msg };
        }
        const user = findUser(json);
        // Auth endpoint = cookie-based (no token fallback). The access token in
        // the response body is kept IN MEMORY for the other endpoints' Bearer.
        const token = authSession.findAccessToken(json) || null;
        authSession.setSession({
          token,
          refreshToken: json?.refreshToken || json?.data?.refreshToken || null,
          role: "admin",
          user,
        });
        persistAdminSession({ email, user });
        // A fresh session must not read another session's cached responses.
        invalidateAdminCache();
        set({
          loginLoading: false,
          loginError: null,
          isAuthenticated: true,
          token,
          isAdmin: true,
          user: user || null,
        });
        return { success: true };
      } catch (err) {
        set({ loginLoading: false, loginError: "Server error, please try again later." });
        return { success: false, error: "Server error, please try again later." };
      }
    },

    /** Clear the in-memory session and the stored admin keys. */
    logout: async () => {
      await authSession.logout("admin");
      clearAdminSession();
      invalidateAdminCache();
      set({
        isAuthenticated: false,
        token: null,
        isAdmin: false,
        user: null,
        loginError: null,
      });
    },
  };
}

export const authActions = createAuthActions;
export default createAuthActions;
